import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiPost } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import Picker from '../../../src/components/Picker';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight } from '../../../src/utils/theme';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

/** AddVehicleScreen - add a vehicle member to a fleet account. */
export default function AddVehicleScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const { fleetId } = useLocalSearchParams();
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [licensePlate, setLicensePlate] = useState('');
  const [state, setState] = useState('TX');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');
  const [nickname, setNickname] = useState('');
  const [department, setDepartment] = useState('');
  const [monthlyWashLimit, setMonthlyWashLimit] = useState('');
  const [monthlySpendingLimit, setMonthlySpendingLimit] = useState('');

  const router = useRouter();
  const toast = useToast();

  const handleSubmit = async () => {
    if (!licensePlate.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'License plate is required' });
      return;
    }
    if (!make.trim() || !model.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Make and model are required' });
      return;
    }

    try {
      setSubmitting(true);
      const body: any = {
        licensePlate: licensePlate.trim().toUpperCase(),
        state,
        make,
        model,
        year: year ? parseInt(year) : undefined,
        color,
        nickname,
        department,
      };

      if (monthlyWashLimit) {
        body.monthlyWashLimit = parseInt(monthlyWashLimit);
      }
      if (monthlySpendingLimit) {
        body.monthlySpendingLimit = Math.round(parseFloat(monthlySpendingLimit) * 100);
      }

      await apiPost(`/api/fleet/accounts/${fleetId}/vehicles`, body);
      toast.show({ type: 'success', title: 'Success', message: 'Member added' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const stateOptions = US_STATES.map(s => ({ label: s, value: s }));

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
      {/* Form */}
      <Card>
        <Text style={styles.sectionTitle}>Member Information</Text>

        <View style={styles.row}>
          <Input
            label="License Plate *"
            value={licensePlate}
            onChangeText={setLicensePlate}
            placeholder="ABC1234"
            autoCapitalize="characters"
            style={{ flex: 1 }}
          />
          <View style={{ width: spacing.md }} />
          <Picker
            label="State *"
            value={state}
            onValueChange={setState}
            options={stateOptions}
          />
        </View>

        <Input
          label="Make *"
          value={make}
          onChangeText={setMake}
          placeholder="e.g., Ford"
        />

        <Input
          label="Model *"
          value={model}
          onChangeText={setModel}
          placeholder="e.g., F-150"
        />

        <View style={styles.row}>
          <Input
            label="Year"
            value={year}
            onChangeText={setYear}
            placeholder="2024"
            keyboardType="numeric"
            maxLength={4}
            style={{ flex: 1 }}
          />
          <View style={{ width: spacing.md }} />
          <Input
            label="Color"
            value={color}
            onChangeText={setColor}
            placeholder="e.g., White"
            style={{ flex: 1 }}
          />
        </View>

        <Input
          label="Nickname"
          value={nickname}
          onChangeText={setNickname}
          placeholder="e.g., Truck #1 or Sales Van"
        />

        <Input
          label="Department"
          value={department}
          onChangeText={setDepartment}
          placeholder="e.g., Sales, Delivery"
        />

        <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>Limits (Optional)</Text>

        <Input
          label="Monthly Wash Limit"
          value={monthlyWashLimit}
          onChangeText={setMonthlyWashLimit}
          placeholder="e.g., 10"
          keyboardType="numeric"
        />

        <Input
          label="Monthly Spending Limit ($)"
          value={monthlySpendingLimit}
          onChangeText={setMonthlySpendingLimit}
          placeholder="e.g., 200.00"
          keyboardType="numeric"
        />

        <View style={styles.actions}>
          <Button
            title="Cancel"
            onPress={() => router.back()}
            variant="outline"
            style={{ flex: 1 }}
          />
          <Button
            title="Add Member"
            onPress={handleSubmit}
            loading={submitting}
            style={{ flex: 1 }}
          />
        </View>
      </Card>

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },

  sectionTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.md,
  },

  row: { flexDirection: 'row', alignItems: 'flex-start' },

  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
